import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";

type Cert = {
  id: string;
  courseId: string;
  courseTitle: string;
  certificateNumber: string;
  pdfUrl: string;
  issuedAt: string;
};

export function Certificates() {
  const [certs, setCerts] = useState<Cert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/certificates")
      .then((res) => {
        if (res.data.success) setCerts(res.data.data || []);
      })
      .catch(() => setCerts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-full bg-stone/40">
      <div className="bg-ink-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Dashboard
          </Link>
          <p className="text-gold-300/90 font-semibold text-xs tracking-[0.2em] uppercase mt-6">
            Achievements
          </p>
          <h1 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight mt-2">
            My certificates
          </h1>
          <p className="mt-2 text-slate-400 text-sm max-w-xl">
            Download and share your earned certificates. Complete all lessons in a course (including each lesson quiz at
            the course passing score) to earn one.
          </p>
          {!loading && certs.length > 0 && (
            <p className="mt-3 text-slate-500 text-sm">
              {certs.length} certificate{certs.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-gold-400/30 to-transparent" aria-hidden />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse rounded-2xl border border-gray-200 bg-white p-6 h-32" />
            ))}
          </div>
        ) : certs.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 md:p-16 text-center shadow-soft">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gold-400/10 border border-gold-400/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-gold-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="font-serif text-xl font-semibold text-ink-950 mt-6">
              No certificates yet
            </h2>
            <p className="mt-2 text-gray-600 max-w-sm mx-auto">
              Complete all lessons in a course. Your instructor will review your work and issue your certificate.
            </p>
            <Link
              to="/dashboard"
              className="mt-8 inline-flex items-center gap-2 rounded-lg bg-gold-400 px-6 py-3.5 text-ink-950 font-semibold hover:bg-gold-300 transition-colors shadow-md"
            >
              Back to dashboard
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
            </Link>
          </div>
        ) : (
          <ul className="space-y-4">
            {certs.map((c) => (
              <li
                key={c.id}
                className="rounded-2xl border border-gray-200 bg-white shadow-soft hover:shadow-soft-lg transition-all overflow-hidden flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 p-6"
              >
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gold-400/10 text-gold-600">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-serif text-xl font-semibold text-ink-950 tracking-tight">
                      {c.courseTitle}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      ID: {c.certificateNumber}
                    </p>
                    <p className="text-sm text-gray-500">
                      Issued {new Date(c.issuedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <a
                  href={c.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-gold-400 px-5 py-2.5 text-ink-950 font-semibold hover:bg-gold-300 transition-colors shadow-sm shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Download PDF
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
